// BEGIN CHANGE: Bulk Upload Dialog Component Logic
import { Component } from '@angular/core';
import { MatDialogRef, MatDialogModule } from '@angular/material/dialog';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Store } from '@ngrx/store';
import { firstValueFrom } from 'rxjs';
import { UserState } from '../../../store/user/user.model';
import { PresignUploadService } from '../../../services/presign-upload.service';
import { environment } from '../../../../environments/environment';
import * as pdfjsLib from 'pdfjs-dist';

interface DisplayItem {
  file: File;
  status: 'pending' | 'uploading' | 'success' | 'error' | 'validating' | 'invalid';
  progress: number;
  error?: string;
  type: 'pdf' | 'csv';
}

@Component({
  selector: 'app-bulk-upload-dialog',
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    MatDialogModule
  ],
  templateUrl: './bulk-upload-dialog.component.html',
  styleUrls: ['./bulk-upload-dialog.component.css']
})
export class BulkUploadDialogComponent {
  pdfItems: DisplayItem[] = [];
  csvItem: DisplayItem | null = null;
  uploading = false;
  completed = false;
  overall = 0;
  validationMsg = '';
  

  constructor(
    private dialogRef: MatDialogRef<BulkUploadDialogComponent>,
    private presign: PresignUploadService,
    private store: Store<{ user: UserState }>
  ) {}

  async onSelectPdfs(evt: Event) {
    const input = evt.target as HTMLInputElement;
    if (!input.files) return;
    const files = Array.from(input.files);
    for (const f of files) {
      if (f.type !== 'application/pdf' && f.type !== 'application/octet-stream') {
        continue;
      }
      const item: DisplayItem = { 
        file: f, 
        status: 'validating', 
        progress: 0, 
        type: 'pdf' 
      };
      this.pdfItems.push(item);
      this.processFileValidation(item);
    }
    input.value = '';
    this.validate();
  }

  // New helper to handle item state updates based on validation
  async processFileValidation(item: DisplayItem) {
    const result = await this.validatePdfStrict(item.file);
    
    if (result.valid) {
      item.status = 'pending';
    } else {
      item.status = 'invalid';
      item.error = result.error;
    }
    
    // Refresh global validation state (e.g. enable/disable Upload button)
    this.validate();
  }

  // Simulated PDF validation logic
  private async validatePdfStrict(file: File): Promise<{ valid: boolean, error?: string }> {
    try {
      if (!file || file.size < 5) {
        return { valid: false, error: 'File too small' };
      }
      const header = await this.readFileHeader(file, 5);
      const headerText = new TextDecoder().decode(header);
      if (!headerText.startsWith('%PDF-')) {
        return { valid: false, error: 'Invalid PDF header' };
      }

      const arrayBuffer = await this.readFileAsArrayBuffer(file);
      const loadingTask = (pdfjsLib as any).getDocument({ data: arrayBuffer });
      const pdf = await loadingTask.promise;

      if (!pdf.numPages || pdf.numPages === 0) {
        return { valid: false, error: 'PDF has 0 pages' };
      }
      const firstPage = await pdf.getPage(1);
      if(!firstPage) {
         return { valid: false, error: 'Cannot read page 1' };
      }

      return { valid: true };

    } catch (error: any) {
      console.error('PDF validation failed:', error);
      return { valid: false, error: 'Corrupt or Invalid PDF' };
    }

  }

  private readFileHeader(file: File, length: number): Promise<Uint8Array> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      const blob = file.slice(0, length);
      reader.onload = () => {
        if (reader.result) resolve(new Uint8Array(reader.result as ArrayBuffer));
        else reject(new Error('Read error'));
      };
      reader.onerror = reject;
      reader.readAsArrayBuffer(blob);
    });
  }

  private readFileAsArrayBuffer(file: File): Promise<ArrayBuffer> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        if (reader.result) resolve(reader.result as ArrayBuffer);
        else reject(new Error('Read error'));
      };
      reader.onerror = reject;
      reader.readAsArrayBuffer(file);
    });
  }

  onSelectCsv(evt: Event) {
    const input = evt.target as HTMLInputElement;
    if (!input.files || !input.files[0]) return;
    const f = input.files[0];
    if (!f.name.toLowerCase().endsWith('.csv')) {
      this.validationMsg = 'Selected file must have .csv extension.';
      input.value = '';
      return;
    }
    this.csvItem = { file: f, status: 'pending', progress: 0, type: 'csv' };
    input.value = '';
    this.validate();
  }

  removePdf(idx: number) {
    this.pdfItems.splice(idx, 1);
    this.validate();
  }

  removeCsv() {
    this.csvItem = null;
    this.validate();
  }

  validate() { 
    const isValidating = this.pdfItems.some(i => i.status === 'validating');
    const hasErrors = this.pdfItems.some(i => i.status === 'error');
    const hasPdf = this.pdfItems.length > 0;
    const hasCsv = !!this.csvItem;

    if (isValidating) {
        this.validationMsg = 'Validating files...';
    } else if (hasErrors) {
        this.validationMsg = 'Please remove invalid files.';
    } else if (!hasPdf) {
        this.validationMsg = 'Add at least one PDF.';
    } else if (!hasCsv) {
        this.validationMsg = 'Add a CSV file.';
    } else {
        this.validationMsg = '';
    }
  }

  canUpload(): boolean {
    return !this.uploading && !this.completed && this.pdfItems.length > 0 && !!this.csvItem && !this.validationMsg;
  }

  async startUpload() {
    if (!this.canUpload()) return;
    this.uploading = true;
    const allItems: DisplayItem[] = [...this.pdfItems, this.csvItem!];
    const total = allItems.length;

    const userState = await firstValueFrom(this.store.select('user'));
    const userEmail = userState.userInfo?.email || 'unknown';
    const username = userEmail.split('@')[0];
    
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    const timestamp = `${year}${month}${day}${hours}${minutes}`;
    
    const sessionId = `${username}_${timestamp}`;

    // Helper to upload a single item
    const uploadItem = async (item: DisplayItem) => {
      item.status = 'uploading';
      try {
        const { url } = await this.presign.requestPresignedForFile(item.file.name, item.file.type, sessionId);
        await this.presign.uploadFileToS3Put(url, item.file, (pct: number) => {
          item.progress = pct;
          this.computeOverall(allItems, total);
        });
        item.status = 'success';
        item.progress = 100;
      } catch (e: any) {
        item.status = 'error';
        item.error = e?.message || 'Upload failed';
      }
      this.computeOverall(allItems, total);
    };

    // Execute uploads with concurrency limit to prevent browser/network overload
    const CONCURRENCY_LIMIT = 5;
    const queue = [...allItems];
    
    const processQueue = async () => {
      while (queue.length > 0) {
        const item = queue.shift();
        if (item) {
          await uploadItem(item);
        }
      }
    };

    const activeWorkers = Array(Math.min(CONCURRENCY_LIMIT, allItems.length))
      .fill(null)
      .map(() => processQueue());

    await Promise.all(activeWorkers);

    // Check if all files uploaded successfully
    const allSuccess = allItems.every(i => i.status === 'success');

    if (allSuccess) {
      try {
        // Generate MANIFEST.json
        const manifestData = {
          user_id: username,
          timestamp: timestamp,
          folder_name: sessionId,
          files: allItems.map(item => ({
            file_name: item.file.name,
            content_type: item.file.type
          })),
          created_at: new Date().toISOString()
        };

        const manifestBlob = new Blob([JSON.stringify(manifestData, null, 2)], { type: 'application/json' });
        const manifestFile = new File([manifestBlob], 'MANIFEST.json', { type: 'application/json' });

        // Upload MANIFEST.json
        const { url } = await this.presign.requestPresignedForFile(manifestFile.name, manifestFile.type, sessionId);
        await this.presign.uploadFileToS3Put(url, manifestFile);
        
        console.log('MANIFEST.json uploaded successfully');
      } catch (e) {
        console.error('Failed to upload MANIFEST.json', e);
      }
    }

    this.uploading = false;
    this.completed = allSuccess;
    this.computeOverall(allItems, total);
  }

  computeOverall(items: DisplayItem[], total: number) {
    const sum = items.reduce((acc, it) => acc + it.progress, 0);
    this.overall = Math.round(sum / total);
  }

  close() {
    this.dialogRef.close(this.completed);
  }
}
// END CHANGE
